import {
  JsObject,
  Coordinates,
  LocationDescriptor,
  Prices,
  IIdBase,
  IdEnum,
  FiatPrice,
} from "../common";
import { IIdFlight } from "../flights-module";

export type CarsAvailabilityRequest = CarsFollowUpSearch | CarsInitialSearch;

export enum CarsTripCategory {
  DIFFERENT_DROP_OFF = "different_drop_off",
  SAME_AS_DROP_OFF = "same_as_drop_off",
}

export enum CarsAvailabilityRequestEnum {
  FollowUpSearch = "FollowUpSearch",
  InitialSearch = "InitialSearch",
}

export type CarsAvailabilityResponse = Results;

export enum CarsAvailabilityResponseEnum {
  Results = "Results",
}

export interface CarShopCancellationPolicy {
  hoursBeforePickUp?: number;
  description?: string;
  CancellationPolicy: CarsCancellationPolicyEnum;
}
export interface CarsCancellationPolicy {
  desc: string;
  free: boolean;
}

export enum CarsCancellationPolicyEnum {
  FreeCancellation = "FreeCancellation",
  NonRefundable = "NonRefundable",
  PartiallyRefundable = "PartiallyRefundable",
  Conditional = "Conditional",
  ConditionalAfterBooking = "ConditionalAfterBooking",
}

export interface CarsFollowUpSearch {
  moreToken: string;
  progressiveConfig: ProgressiveConfig;
  AvailabilityRequest: CarsAvailabilityRequestEnum;
}

export type GooglePlaceId = string;

export type GroundSelection = CarsLocation | Place;

export enum GroundSelectionEnum {
  Location = "Location",
  Place = "Place",
}

export type VehicleAvailabilityId = string;

export interface VehicleIdFilter {
  vehicleAvailabilityIds: VehicleAvailabilityId[];
  AvailabilityFilter: AvailabilityFilterEnum;
}
export type AvailabilityFilter = VehicleIdFilter;

export enum AvailabilityFilterEnum {
  VehicleIdFilter = "VehicleIdFilter",
}

export interface CarsInitialSearch {
  pickupSelection: CarsAutocompleteId;
  pickupTime: string;
  dropOffSelection: CarsAutocompleteId;
  dropOffTime: string;
  driverAge?: number;
  filter?: AvailabilityFilter;
  AvailabilityRequest: CarsAvailabilityRequestEnum;
}

export interface CarsLocation {
  descriptor: LocationDescriptor;
  center?: Coordinates;
  GroundSelection: GroundSelectionEnum.Location;
}

export interface Place {
  placeId: GooglePlaceId;
  searchTerm: string;
  placeTypes: string[];
  GroundSelection: GroundSelectionEnum.Place;
}

export interface ProgressiveConfig {
  pageSize?: number;
}

export interface Results {
  vehicles: VehicleAvailability[];
  context: ResultsContext;
  nextPageToken?: string;
  AvailabilityResponse: CarsAvailabilityResponseEnum;
}

export interface ResultsContext {
  vendors: { [key: string]: Vendor };
  suppliers: { [key: string]: Supplier };
  opaqueShopRequestContext: string;
}

export interface Supplier {
  code: string;
  name: string;
  logo: string;
  contactSupport?: string;
}

export interface TimeAndPlace {
  time: string;
  metersToRequested?: number;
  address: string;
  coordinates: Coordinates;
}

export enum TransmissionTypeEnum {
  Automatic = "Automatic",
  Cvt = "Cvt",
  Manual = "Manual",
}

export enum ImageKind {
  Original = "original",
  Large = "large",
  Small = "small",
  Illustration = "illustration",
}

export interface Vehicle {
  airConditioning: boolean;
  passengerCapacity: number;
  kind: VehicleKindEnum;
  transmissionType: TransmissionTypeEnum;
  bagCapacity: { [key: string]: number };
  images?: { [key: string]: string };
  id: string;
  name: string;
}

export interface FuelPolicy {
  kind: FuelPolicyKind;
  desc: string;
}

export enum FuelPolicyKind {
  PrepayRefunds = "PrepayRefunds",
  PrepayNoRefunds = "PrepayNoRefunds",
  FreeTank = "FreeTank",
  Unknown = "Unknown",
  ReturnSame = "ReturnSame",
  FullToFull = "FullToFull",
  MultipleOptionsAtDesk = "MultipleOptionsAtDesk",
}

export interface VehicleAvailability {
  supplierRef: string;
  vehicle: Vehicle;
  pickUp: TimeAndPlace;
  dropOff: TimeAndPlace;
  hasUnlimitedMileage: boolean;
  cancellationPolicy: CarShopCancellationPolicy;
  totalCharge: FiatPrice;
  baseRate: FiatPrice;
  fees: FiatPrice;
  payAtDesk: FiatPrice;
  payNow: Prices;
  vendorRef: string;
  collisionDamageWaiver?: boolean;
  fuelPolicy?: FuelPolicy;
  opaqueShopRequest: string;
}

export enum VehicleKindEnum {
  Truck = "Truck",
  Small = "Small",
  Mystery = "Mystery",
  SUV = "SUV",
  Mini = "Mini",
  Intermediate = "Intermediate",
  Van = "Van",
  Luxury = "Luxury",
  FullSize = "FullSize",
}

export interface Vendor {
  code: string;
  name: string;
  contactSupport?: string;
}

export interface CarsAutocompleteResponse {
  category: Category;
  label: string;
  results: CarsAutocompleteResult[];
}

export interface CarsAutocompleteResult {
  id: CarsAutocompleteId;
  label: string;
  trackingProperties?: JsObject;
}

// note: see Id trait in /Tysons/b2b-common/src/main/scala/com/hopper/b2b/model/autocomplete/Autocomplete.scala
export type CarsAutocompleteId = IIdFlight | IIdCar;

export interface IIdCar extends IIdBase {
  Id: IdEnum.Grounds;
  groundSelection: GroundSelection;
}

enum Category {
  Airports = "Airports",
  Cities = "Cities",
  HotelMarkets = "HotelMarkets",
  Places = "Places",
  RecentSearches = "RecentSearches",
  EmptyMatch = "EmptyMatch",
}

export interface Charge {
  kind: ChargeKind;
  amount: Prices;
}

export enum ChargeKind {
  Extra = "Extra",
  ExtraDaily = "ExtraDaily",
  ExtraOriginal = "ExtraOriginal",
  TotalWithExtra = "TotalWithExtra",
}

export interface Link {
  kind: LinkKind;
  link: string;
}
export enum LinkKind {
  PostBookingPolicyWordingURI = "PostBookingPolicyWordingURI",
  PreBookingPolicyWordingURI = "PreBookingPolicyWordingURI",
  PostBookingKeyFactsURI = "PostBookingKeyFactsURI",
  PreBookingKeyFactsURI = "PreBookingKeyFactsURI",
  PostBookingDIPDanniCertificate = "PostBookingDIPDanniCertificate",
  PreBookingDIPDanniCertificate = "PreBookingDIPDanniCertificate",
  PostBookingCertificateURI = "PostBookingCertificateURI",
}

export interface Remark {
  key: string;
  value: string;
}

export interface CarShopRequest {
  opaqueShopRequestContext: string;
  opaqueShopRequestVehicle: string;
}
export interface Charge {
  kind: ChargeKind;
  amount: Prices;
}

export interface CarShopResponse {
  elements: ShopResponseElement[];
  vendorMessages?: VendorMessages;
  collisionDamageWaiver?: boolean;
  needsDriverDetails?: boolean;
  opaqueBookRequest: string;
  description: string;
}

export interface ShopResponseElement {
  id: string;
  remarks: Remark[];
  name: string;
  description: string;
  kind: ShopResponseElementKind;
  prePayable: boolean;
  isDefault: boolean;
  charges: Charge[];
  links: Link[];
  trackingProperties?: object;
  sellingPoints?: string[];
  disclaimer?: string;
  contactSupport?: string;
}

export enum ShopResponseElementKind {
  Other = "Other",
  InsuranceExternal = "InsuranceExternal",
  Amenities = "Amenities",
  ExtraDriver = "ExtraDriver",
  BabyOnBoard = "BabyOnBoard",
  General = "General",
  Insurance = "Insurance",
}

export interface VendorMessageSection {
  desc: string;
  details: VendorMessageSubSection[];
  linkMsg?: string;
}

export interface VendorMessageSubSection {
  title?: string;
  text: string;
  displayText: string;
}

export interface VendorMessages {
  bookingConditions?: VendorMessageSection;
  carRentalPolicy?: VendorMessageSection;
  importantInfo?: VendorMessageSection;
  pickUpDirections?: VendorMessageSection;
}
